import { useState } from 'react';

import {
  Alert,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Switch,
  Tabs,
  Text,
} from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';

import {
  LogoPaper,
  PageTitle,
  ProviderAgreementList,
  ProviderAgreementModal,
  ProviderRateModal,
  ProviderRatesTable,
} from '@/components';
import { todayIso } from '@/components/ProviderCosts/format';
import { useDisplayCurrency, useEconomicsSummary, useProviderCosts } from '@/hooks';
import {
  EXTERNAL_PROVIDERS,
  providerLabel,
  type ProviderAgreement,
  type ProviderRate,
} from '@/types/provider-cost';
import { presetPeriod } from './CogsDashboard.page';

type PendingDelete =
  | { kind: 'rate'; rate: ProviderRate }
  | { kind: 'agreement'; agreement: ProviderAgreement };

/**
 * Admin: what each external provider costs us per unit, and the prepaid
 * agreements most of those rates come from. Usage is costed from these on the
 * next refresh of the cost figures; usage with no rate shows as unrated.
 */
export function ProviderRatesPage() {
  const [tab, setTab] = useState<string | null>('rates');
  const [provider, setProvider] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const {
    rates,
    agreements,
    isLoading,
    error,
    createRate,
    updateRate,
    deleteRate,
    createAgreement,
    replaceAgreement,
    deleteAgreement,
  } = useProviderCosts({
    provider: provider ?? undefined,
    activeOn: showHistory ? undefined : todayIso(),
  });

  // Usage the cost figures could not price, so the gaps on this card show.
  const [displayCurrency] = useDisplayCurrency();
  const [periodFrom, periodTo] = presetPeriod('last-12');
  const { summary: costSummary } = useEconomicsSummary({ displayCurrency, periodFrom, periodTo });
  const missingRates = (costSummary?.coverage.unratedReasons ?? []).filter((r) =>
    r.reason.startsWith('no rate for')
  );

  const [rateModal, setRateModal] = useState<{ rate: ProviderRate | null } | null>(null);
  const [agreementModal, setAgreementModal] = useState<{
    agreement: ProviderAgreement | null;
  } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [highlighted, setHighlighted] = useState<string | null>(null);

  const openAgreement = (agreementId: string) => {
    setHighlighted(agreementId);
    setTab('agreements');
    requestAnimationFrame(() =>
      document.getElementById(`agreement-${agreementId}`)?.scrollIntoView({ block: 'center' })
    );
  };

  const confirmDelete = async () => {
    if (!pendingDelete) {
      return;
    }
    try {
      if (pendingDelete.kind === 'rate') {
        await deleteRate(pendingDelete.rate.id);
      } else {
        await deleteAgreement(pendingDelete.agreement.id);
      }
      setPendingDelete(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Could not delete');
    }
  };

  if (isLoading && rates.length === 0 && agreements.length === 0) {
    return (
      <Center h={300}>
        <Loader />
      </Center>
    );
  }

  return (
    <Container size="xl" py="md">
      <ProviderRateModal
        opened={rateModal !== null}
        rate={rateModal?.rate}
        onClose={() => setRateModal(null)}
        onCreate={createRate}
        onUpdate={updateRate}
      />
      <ProviderAgreementModal
        opened={agreementModal !== null}
        agreement={agreementModal?.agreement}
        onClose={() => setAgreementModal(null)}
        onCreate={createAgreement}
        onReplace={replaceAgreement}
      />
      <Modal
        opened={pendingDelete !== null}
        onClose={() => {
          setPendingDelete(null);
          setDeleteError(null);
        }}
        title={pendingDelete?.kind === 'agreement' ? 'Delete agreement' : 'Delete rate'}
      >
        <Stack>
          <Text size="sm">
            {pendingDelete?.kind === 'agreement'
              ? `Delete this ${providerLabel(pendingDelete.agreement.provider)} agreement and the rates it put on the rate card? Usage it priced becomes unrated on the next refresh.`
              : 'Delete this rate? Usage it priced becomes unrated on the next refresh.'}
          </Text>
          {deleteError ? (
            <Alert color="red" title="Not deleted">
              {deleteError}
            </Alert>
          ) : null}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setPendingDelete(null)}>
              Keep
            </Button>
            <Button color="red" onClick={() => void confirmDelete()}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Stack>
        <Stack gap={4}>
          <PageTitle title="Provider rates" />
          <Text size="sm" c="dimmed" maw={760}>
            What each external provider costs per unit. Most providers are paid as a prepaid pool:
            record the agreement and its rate — the price divided over the allowance — is added
            here. Usage is costed from these rates on the next refresh of the cost figures.
          </Text>
        </Stack>

        {error ? <Text c="red">Could not load the rate card: {error.message}</Text> : null}

        {missingRates.length > 0 ? (
          <Alert
            color="orange"
            title="Usage with no rate, last 12 months"
            data-testid="missing-rates"
          >
            <Stack gap={2}>
              <Text size="sm">
                These are recorded but not costed, so cost figures are a floor until each has a
                rate. A rate added with an earlier start date prices past usage too.
              </Text>
              {missingRates.map((r) => (
                <Text key={r.reason} size="sm">
                  {r.reason.replace('no rate for ', '')}: {r.lines} line(s)
                </Text>
              ))}
            </Stack>
          </Alert>
        ) : null}

        <Tabs value={tab} onChange={setTab}>
          <Group justify="space-between" mb="sm">
            <Tabs.List>
              <Tabs.Tab value="rates">Rates</Tabs.Tab>
              <Tabs.Tab value="agreements">Agreements</Tabs.Tab>
            </Tabs.List>
            <Group>
              <Select
                placeholder="All providers"
                aria-label="Provider"
                clearable
                data={EXTERNAL_PROVIDERS.map((p) => ({ value: p, label: providerLabel(p) }))}
                value={provider}
                onChange={setProvider}
                w={200}
              />
              {tab === 'rates' ? (
                <>
                  <Switch
                    label="Include past and future rates"
                    checked={showHistory}
                    onChange={(e) => setShowHistory(e.currentTarget.checked)}
                  />
                  <Button
                    leftSection={<IconPlus size={16} />}
                    onClick={() => setRateModal({ rate: null })}
                  >
                    Add rate
                  </Button>
                </>
              ) : (
                <Button
                  leftSection={<IconPlus size={16} />}
                  onClick={() => setAgreementModal({ agreement: null })}
                >
                  Record agreement
                </Button>
              )}
            </Group>
          </Group>

          <Tabs.Panel value="rates">
            <LogoPaper>
              <ProviderRatesTable
                rates={rates}
                onEdit={(rate) => setRateModal({ rate })}
                onDelete={(rate) => setPendingDelete({ kind: 'rate', rate })}
                onOpenAgreement={openAgreement}
              />
            </LogoPaper>
          </Tabs.Panel>
          <Tabs.Panel value="agreements">
            <ProviderAgreementList
              agreements={agreements}
              highlightedId={highlighted}
              onEdit={(agreement) => setAgreementModal({ agreement })}
              onDelete={(agreement) => setPendingDelete({ kind: 'agreement', agreement })}
            />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}
